import {
  PICKUP_TIME_SLOTS,
  addDaysToIsoDate,
  getIsoWeekday,
} from "@/lib/pickup-timezone";

export const GREEK_HOUSE_OPTIONS = [
  "Alpha Phi House",
  "Chi Omega House",
  "Delta Delta Delta House",
  "Delta Gamma House",
  "Gamma Phi Beta House",
  "Kappa Alpha Theta House",
  "Kappa Delta House",
  "Kappa Kappa Gamma House",
  "Phi Kappa Psi House",
  "Pi Beta Phi House",
  "Sigma Alpha Epsilon House",
  "Sigma Chi House",
  "Sigma Nu House",
  "Theta Chi House",
  "Zeta Beta Tau House",
] as const;

const GREEK_HOUSE_SET: ReadonlySet<string> = new Set(GREEK_HOUSE_OPTIONS);

/**
 * Street addresses (Los Angeles, CA 90024) from chapter / national sites and
 * the Westwood Neighborhood Council's chapter-house list, 2026-09. Runner
 * directions use these so Google Maps routes to the actual house; the lat/lng
 * in ucla-buildings.ts for these houses are approximate placeholders used only
 * for rough route ordering.
 */
export const GREEK_HOUSE_ADDRESSES: Readonly<Record<(typeof GREEK_HOUSE_OPTIONS)[number], string>> = {
  "Alpha Phi House": "714 Hilgard Ave",
  "Chi Omega House": "708 Hilgard Ave",
  "Delta Delta Delta House": "862 Hilgard Ave",
  "Delta Gamma House": "652 Hilgard Ave",
  "Gamma Phi Beta House": "616 Hilgard Ave",
  "Kappa Alpha Theta House": "736 Hilgard Ave",
  "Kappa Delta House": "800 Hilgard Ave",
  "Kappa Kappa Gamma House": "744 Hilgard Ave",
  // UCLA chapter: https://phipsibruins.com/contact-us
  "Phi Kappa Psi House": "613 Gayley Ave",
  "Pi Beta Phi House": "700 Hilgard Ave",
  "Sigma Alpha Epsilon House": "655 Gayley Ave",
  "Sigma Chi House": "459 Gayley Ave",
  "Sigma Nu House": "601 Gayley Ave",
  "Theta Chi House": "663 Gayley Ave",
  "Zeta Beta Tau House": "10924 Strathmore Dr",
};

export const greekHouseAddress = (building: string): string | null =>
  isGreekHouse(building) ? GREEK_HOUSE_ADDRESSES[building as (typeof GREEK_HOUSE_OPTIONS)[number]] : null;

export const isGreekHouse = (building: string): boolean =>
  GREEK_HOUSE_SET.has(building);

export const UCLA_HOUSING_BUILDING_OPTIONS = [
  "De Neve Plaza",
  "De Neve Plaza - Acacia",
  "De Neve Plaza - Birch",
  "De Neve Plaza - Cedar",
  "De Neve Plaza - Dogwood",
  "De Neve Plaza - Evergreen",
  "De Neve Plaza - Fir",
  "De Neve Plaza - Gardenia",
  "De Neve Plaza - Holly",
  "Dykstra Hall",
  "Hedrick Hall",
  "Rieber Hall",
  "Sproul Hall",
  "Centennial Hall",
  "Olympic Hall",
  "Sproul Cove",
  "Sproul Landing",
  "Hedrick Summit",
  "Rieber Terrace",
  "Rieber Vista",
  "Hitch Suites (A, B, C, D)",
  "Saxon Suites (E, F, G, H, J)",
  "Sunset Village",
  "Sunset Village - Canyon Point (A)",
  "Sunset Village - Delta Terrace (B)",
  "Sunset Village - Courtside (C)",
  ...GREEK_HOUSE_OPTIONS,
] as const;

/** Flat customer rate per pound for wash & fold. */
export const WASH_CUSTOMER_RATE_PER_LB = 1.85;

export const WASH_INTERNAL_COST_PER_LB = 1.4;

const toRoundedCurrency = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/** Minimum billable weight per bag - anything below rounds up. */
export const WASH_MINIMUM_LBS = 12;

/** Apply the per-bag minimum weight to a weight value. */
export const applyWashMinimum = (weightLb: number) =>
  !Number.isFinite(weightLb) || weightLb <= 0 ? 0 : Math.max(weightLb, WASH_MINIMUM_LBS);

export const getWashCustomerRatePerPound = (weightLb: number) =>
  !Number.isFinite(weightLb) || weightLb <= 0 ? 0 : WASH_CUSTOMER_RATE_PER_LB;

export const getWashCustomerCharge = (weightLb: number) => {
  if (!Number.isFinite(weightLb) || weightLb <= 0) return 0;
  return toRoundedCurrency(applyWashMinimum(weightLb) * WASH_CUSTOMER_RATE_PER_LB);
};

export const getWashCustomerChargeCents = (weightLb: number) =>
  Math.round(getWashCustomerCharge(weightLb) * 100);

export const getWashInternalCost = (weightLb: number) => {
  if (!Number.isFinite(weightLb) || weightLb <= 0) return 0;
  return toRoundedCurrency(applyWashMinimum(weightLb) * WASH_INTERNAL_COST_PER_LB);
};

export const getWashInternalCostCents = (weightLb: number) =>
  Math.round(getWashInternalCost(weightLb) * 100);

/**
 * Flat $1.85/lb across all weights. Kept under the "progressive" name so
 * existing callers (admin spreadsheet cross-bag totals) keep working without
 * an interface change. The per-bag minimum is NOT applied here (callers
 * handle it per-bag).
 */
export const getWashProgressiveCharge = (weightLb: number) => {
  if (!Number.isFinite(weightLb) || weightLb <= 0) return 0;
  return toRoundedCurrency(weightLb * WASH_CUSTOMER_RATE_PER_LB);
};

/** Progressive internal cost (flat rate, applies per-bag minimum). */
export const getWashProgressiveInternalCost = (weightLb: number) => {
  if (!Number.isFinite(weightLb) || weightLb <= 0) return 0;
  return toRoundedCurrency(applyWashMinimum(weightLb) * WASH_INTERNAL_COST_PER_LB);
};

export const DRY_CLEANING_MIN_PIECES = 1;

// ── Bag prefix routing ──────────────────────────────────────────────────
// Operational convention: physical bags are pre-tagged with an A* prefix
// for wash & fold and a B* prefix for dry cleaning. The classifier below
// is the single source of truth - if a runner accidentally enters a
// "B29" bag in the wash field (or vice versa), the server re-routes it
// based on prefix instead of trusting the field.

export type BagKind = "wash" | "dry" | "unknown";

export const classifyBagIdByPrefix = (bagId: string): BagKind => {
  const t = String(bagId ?? "").trim().toUpperCase();
  if (!t) return "unknown";
  if (t[0] === "A") return "wash";
  if (t[0] === "B") return "dry";
  return "unknown";
};

export type BagRoutingResult = {
  /** Final wash bag IDs (A-prefixed, plus any unknowns originally entered as wash). */
  washBagIds: string[];
  /** Final dry cleaning bag ID (single). Empty string if none. */
  dryBagId: string;
  /** Bags moved from wash field → dry slot because of B-prefix. */
  movedToDry: string[];
  /** Bags moved from dry field → wash bucket because of A-prefix. */
  movedToWash: string[];
  /** B-prefix bags that couldn't fit (more than one B-bag entered). */
  extraDryBags: string[];
};

/**
 * Re-route runner / admin entered bag IDs into wash and dry buckets based
 * on the A-prefix / B-prefix convention. Bags with no A or B prefix stay
 * in whichever field they were originally entered (treated as legacy).
 *
 * Returns:
 *   - washBagIds: final wash bucket
 *   - dryBagId:   final single dry cleaning slot (first B-bag wins)
 *   - movedToDry / movedToWash / extraDryBags: telemetry the caller can
 *     surface to the operator ("Moved B29 to dry cleaning") and use to
 *     reject conflicts (e.g. multiple B-bags in a single pickup).
 */
export const routeBagsByPrefix = (input: {
  washBagIds: string[];
  dryBagId: string | null | undefined;
}): BagRoutingResult => {
  const movedToDry: string[] = [];
  const movedToWash: string[] = [];
  const extraDryBags: string[] = [];
  const finalWash: string[] = [];
  let finalDry = "";

  const setDry = (bagId: string) => {
    if (!finalDry) {
      finalDry = bagId;
      return true;
    }
    if (finalDry === bagId) return true;
    extraDryBags.push(bagId);
    return false;
  };

  for (const raw of input.washBagIds) {
    const bagId = String(raw ?? "").trim().toUpperCase();
    if (!bagId) continue;
    const kind = classifyBagIdByPrefix(bagId);
    if (kind === "dry") {
      if (setDry(bagId)) movedToDry.push(bagId);
      // If the dry slot was already taken, the bag goes to extraDryBags
      // and the caller treats this as a conflict (don't silently drop).
    } else {
      // wash or unknown: keep in wash bucket
      if (!finalWash.includes(bagId)) finalWash.push(bagId);
    }
  }

  const dryEntered = String(input.dryBagId ?? "").trim().toUpperCase();
  if (dryEntered) {
    const kind = classifyBagIdByPrefix(dryEntered);
    if (kind === "wash") {
      // Originally entered as dry but actually a wash bag.
      if (!finalWash.includes(dryEntered)) finalWash.push(dryEntered);
      movedToWash.push(dryEntered);
    } else {
      // dry or unknown: keep as dry slot. Original-field dry wins over
      // any B-bag that came in via the wash list.
      if (finalDry && finalDry !== dryEntered) {
        // We already had a B-bag from the wash list; the original-field
        // dry takes precedence. Bump the previous one to extra.
        const prev = finalDry;
        finalDry = dryEntered;
        if (!extraDryBags.includes(prev)) extraDryBags.push(prev);
        // Also remove from movedToDry since it didn't actually land.
        const idx = movedToDry.indexOf(prev);
        if (idx >= 0) movedToDry.splice(idx, 1);
      } else if (!finalDry) {
        finalDry = dryEntered;
      }
    }
  }

  return {
    washBagIds: finalWash,
    dryBagId: finalDry,
    movedToDry,
    movedToWash,
    extraDryBags,
  };
};

// ── Off-campus delivery fee ──────────────────────────────────────────────
// B-Laundry serves UCLA's residential Hill plus a 2-mile radius around it.
// Off-campus pickups pay a tiered delivery fee proportional to distance.

/**
 * Approximate centroid of the UCLA dorms (the "Hill"), used as the origin
 * for off-campus delivery distance calculations. Sits between De Neve and
 * the Sunset Village complex; close enough to every Hill building that any
 * dorm-to-dorm variation is within the rounding of the fee tiers.
 */
export const UCLA_HILL_CENTER = { lat: 34.0721, lng: -118.4500 } as const;

/** Maximum off-campus delivery radius in miles. Anything beyond is rejected. */
export const OFF_CAMPUS_MAX_MILES = 2;

/**
 * Tiered delivery fee for off-campus pickups, in cents.
 *
 *   0.0 - 0.5 mi → $6
 *   0.5 - 1.0 mi → $9
 *   1.0 - 1.5 mi → $12
 *   1.5 - 2.0 mi → $15
 *   > 2.0 mi     → null (out of service area)
 *
 * Returns 0 for non-finite or non-positive distances - callers should
 * treat 0 as "no fee" (i.e. on-campus).
 */
export const getOffCampusDeliveryFeeCents = (distanceMiles: number): number | null => {
  if (!Number.isFinite(distanceMiles) || distanceMiles <= 0) return 0;
  if (distanceMiles <= 0.5) return 600;
  if (distanceMiles <= 1.0) return 900;
  if (distanceMiles <= 1.5) return 1200;
  if (distanceMiles <= OFF_CAMPUS_MAX_MILES) return 1500;
  return null;
};

// ── Dry-cleaning-only convenience fee ────────────────────────────────────
// Small dry-clean-only orders eat the same dispatch + handoff cost as
// larger orders without enough pieces to amortize it. We charge a flat
// $4 convenience fee unless the order has 3+ items (sums quantities
// across all line types).

export const DRY_CLEANING_CONVENIENCE_FEE_CENTS = 400;
/** Flat pickup & delivery service fee for wash & fold orders (per pickup). */
export const PICKUP_DELIVERY_FEE_CENTS = 850;
/** Historical fee for unsnapshotted orders and legacy subscription contracts. */
export const LEGACY_PICKUP_DELIVERY_FEE_CENTS = 800;
export const DRY_CLEANING_CONVENIENCE_FEE_THRESHOLD = 3;

/**
 * Returns the convenience fee in cents for the given pickup composition.
 *
 * Fee fires only when:
 *   1. The order is dry-cleaning-only (no wash & fold), AND
 *   2. The total item count (sum of quantities) is below the threshold.
 *
 * Returns 0 in every other case.
 */
export const getDryCleaningConvenienceFeeCents = (input: {
  includesWashFold: boolean;
  includesDryCleaning: boolean;
  totalDryCleaningItems: number;
}): number => {
  if (!input.includesDryCleaning) return 0;
  if (input.includesWashFold) return 0;
  if (input.totalDryCleaningItems >= DRY_CLEANING_CONVENIENCE_FEE_THRESHOLD) return 0;
  return DRY_CLEANING_CONVENIENCE_FEE_CENTS;
};

const DRY_CLEANING_CATALOG_WITH_COST = [
  { name: "Pants", price: 15.0, cost: 5.3, pieces: 1 },
  { name: "Shirts", price: 9.5, cost: 5.3, aliases: ["Shirt"], pieces: 1 },
  { name: "Sweaters", price: 15.0, cost: 5.3, aliases: ["Sweater"], pieces: 1 },
  { name: "Blouse", price: 9.5, cost: 5.3, pieces: 1 },
  { name: "Dress", price: 20.0, cost: 7.9, pieces: 1 },
  { name: "Jacket", price: 15.0, cost: 7.9, pieces: 1 },
  { name: "Blazer", price: 15.0, cost: 7.9, pieces: 1 },
  {
    name: "2pc Suit (Jacket & Pants)",
    price: 35.0,
    cost: 13.5,
    aliases: ["2pc Suit"],
    pieces: 2,
  },
  { name: "Jump Suit", price: 20.0, cost: 10.8, pieces: 1 },
  { name: "Tie", price: 5.0, cost: 3.4, pieces: 1 },
  { name: "Skirt", price: 15.0, cost: 5.3, pieces: 1 },
  { name: "Vest", price: 12.0, cost: 5.2, pieces: 1 },
  { name: "T-Shirt", price: 9.0, cost: 5.2, pieces: 1 },
  { name: "Long Dress", price: 17.0, cost: 10.8, pieces: 1 },
] as const;

const getDryCleaningAliases = (
  item: (typeof DRY_CLEANING_CATALOG_WITH_COST)[number]
) => ("aliases" in item ? [...item.aliases] : []);

export const DRY_CLEANING_CATALOG = DRY_CLEANING_CATALOG_WITH_COST.map(
  ({ name, price }) => ({ name, price })
).filter((item) => item.name !== "Long Dress");

export const DRY_CLEANING_PRICE_MAP: Record<string, number> = Object.fromEntries(
  DRY_CLEANING_CATALOG_WITH_COST.flatMap((item) => [
    [item.name, item.price],
    ...getDryCleaningAliases(item).map((alias) => [alias, item.price]),
  ])
);

export const DRY_CLEANING_COST_MAP: Record<string, number> = Object.fromEntries(
  DRY_CLEANING_CATALOG_WITH_COST.flatMap((item) => [
    [item.name, item.cost],
    ...getDryCleaningAliases(item).map((alias) => [alias, item.cost]),
  ])
);

export const DRY_CLEANING_PIECE_MAP: Record<string, number> = Object.fromEntries(
  DRY_CLEANING_CATALOG_WITH_COST.flatMap((item) => [
    [item.name, item.pieces],
    ...getDryCleaningAliases(item).map((alias) => [alias, item.pieces]),
  ])
);

type DryCleaningLineItem = {
  item_type: string;
  quantity?: number | null;
};

export const getDryCleaningOrderCharge = (items: DryCleaningLineItem[]) =>
  toRoundedCurrency(
    items.reduce((sum, item) => {
      const quantity = Number(item.quantity ?? 0);
      const unitPrice = DRY_CLEANING_PRICE_MAP[item.item_type] ?? 0;
      return sum + Math.max(0, quantity) * unitPrice;
    }, 0)
  );

export const getDryCleaningOrderCost = (items: DryCleaningLineItem[]) =>
  toRoundedCurrency(
    items.reduce((sum, item) => {
      const quantity = Number(item.quantity ?? 0);
      const unitCost = DRY_CLEANING_COST_MAP[item.item_type] ?? 0;
      return sum + Math.max(0, quantity) * unitCost;
    }, 0)
  );

/**
 * Friday returns get extra early-morning slots (8:00 – 9:30) on top of the
 * universal 10:00 – 18:00 set, because the driver has morning availability.
 * Other weekdays do NOT get these slots.
 */
const FRIDAY_EARLY_EXTRA_SLOTS = ["08:00", "08:30", "09:00", "09:30"] as const;
const EARLY_START_WEEKDAYS = new Set([5]); // Fri only

export const DROPOFF_TIME_OPTIONS = new Set<string>([
  ...PICKUP_TIME_SLOTS,
  ...FRIDAY_EARLY_EXTRA_SLOTS,
]);

/**
 * Thursday returns cannot begin before 3:00 PM - driver is in class.
 * Earlier slots in PICKUP_TIME_SLOTS are stripped for those weekdays.
 */
const LATE_START_WEEKDAYS = new Set([4]); // Thu only
const LATE_START_MIN_TIME = "15:00";

export const getDropoffTimeSlotsForDate = (dropoffDate?: string | null): string[] => {
  let base = [...PICKUP_TIME_SLOTS] as string[];
  if (dropoffDate) {
    const weekday = getIsoWeekday(dropoffDate);
    if (weekday !== null && LATE_START_WEEKDAYS.has(weekday)) {
      base = base.filter((t) => t >= LATE_START_MIN_TIME);
    }
    if (weekday !== null && EARLY_START_WEEKDAYS.has(weekday)) {
      base = [...FRIDAY_EARLY_EXTRA_SLOTS, ...base];
    }
  }
  return base;
};

export const isDropoffTimeOption = (value: string, dropoffDate?: string | null) => {
  if (!DROPOFF_TIME_OPTIONS.has(value)) return false;
  const weekday = dropoffDate ? getIsoWeekday(dropoffDate) : null;
  // Friday-early extra slots (08:00–09:30) are ONLY valid on Fridays. Without a
  // date we can't confirm Friday, so reject them by default.
  if ((FRIDAY_EARLY_EXTRA_SLOTS as readonly string[]).includes(value)) {
    if (weekday === null || !EARLY_START_WEEKDAYS.has(weekday)) return false;
  }
  if (weekday !== null && LATE_START_WEEKDAYS.has(weekday) && value < LATE_START_MIN_TIME) {
    return false;
  }
  return true;
};

export const BAG_ID_PATTERN = /^[A-Z0-9][A-Z0-9 _-]{0,38}[A-Z0-9]$/;

export const normalizeBagId = (value: unknown) => {
  const text = String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");
  return text ? text.toUpperCase() : "";
};

export const isBagIdFormatValid = (value: string) =>
  BAG_ID_PATTERN.test(value);

export const normalizeBagIdList = (value: unknown) => {
  const rawValues = Array.isArray(value)
    ? value
    : String(value ?? "").split(/[\r\n,;]+/);
  const normalized: string[] = [];

  for (const item of rawValues) {
    const bagId = normalizeBagId(item);
    if (!bagId || normalized.includes(bagId)) continue;
    normalized.push(bagId);
  }

  return normalized;
};

// ── Return schedule config ──
// Standard returns: 1-2 days after pickup (dry cleaning: 2 days only)
// Late returns: 4-5 days after pickup with a fee
export const STANDARD_RETURN_DAYS = [1, 2] as const;
export const STANDARD_RETURN_DAYS_DRY_CLEANING = [2] as const;
export const LATE_RETURN_DAYS = [4, 5] as const;
export const LATE_RETURN_FEE_PERCENT = 5;

// Earliest date (LA / PDT, ISO) a return may be booked for. Returns are never
// offered or accepted before this date anywhere - the first available return is
// Saturday Jun 6, 2026. Temporary operational floor (no runner coverage before
// then); can be removed once it is safely in the past.
export const RETURN_EARLIEST_DATE = "2026-06-06";

// Additional individual blocked days beyond the floor, if any (e.g. a one-off
// holiday after the floor). Empty for now since the floor covers Jun 5 / Fri.
export const RETURN_BLACKOUT_DATES = new Set<string>([]);

// Extra return days opened up beyond the usual pickup+1/+2 window. This week we
// run returns every day, so every day of the week (LA ISO dates) is offered as
// a return slot - subject to the per-order processing minimum (>= pickup+1 for
// wash, >= pickup+2 for dry cleaning) and the floor. Self-expires: once these
// dates are in the past, the processing-minimum filter drops them. Temporary.
export const EXTRA_RETURN_DATES: readonly string[] = [
  "2026-06-08", "2026-06-09", "2026-06-10", "2026-06-11",
  "2026-06-12", "2026-06-13", "2026-06-14",
];

export const getDropoffOptionsForPickup = (
  preferredDate: string,
  includesDryCleaning?: boolean | null
) => {
  const standardDays = includesDryCleaning ? STANDARD_RETURN_DAYS_DRY_CLEANING : STANDARD_RETURN_DAYS;
  const standard = standardDays
    .map((d) => addDaysToIsoDate(preferredDate, d))
    .filter((v): v is string => Boolean(v));
  const late = LATE_RETURN_DAYS
    .map((d) => addDaysToIsoDate(preferredDate, d))
    .filter((v): v is string => Boolean(v));
  // Earliest a return can land given processing time (wash needs 1 day, dry 2).
  const minReturn = addDaysToIsoDate(preferredDate, includesDryCleaning ? 2 : 1);
  // Open daily slots, but never before the order could actually be ready.
  const extra = minReturn ? EXTRA_RETURN_DATES.filter((d) => d >= minReturn) : [];
  // ISO date strings compare chronologically. Merge the pickup-relative and the
  // open daily slots, drop anything before the earliest allowed return date or
  // a blacked-out day, dedupe, and sort. This is the one place every booking
  // surface (customer picker, server validation, checkout, admin) reads, so the
  // rules apply uniformly.
  return [...new Set([...standard, ...late, ...extra])]
    .filter((d) => d >= RETURN_EARLIEST_DATE && !RETURN_BLACKOUT_DATES.has(d))
    .sort();
};

/** Check if a return date is a late return */
export const isLateReturn = (preferredDate: string, dropoffDate: string) => {
  return LATE_RETURN_DAYS.some((d) => addDaysToIsoDate(preferredDate, d) === dropoffDate);
};

/** Get the standard return dates only (no late) */
export const getStandardDropoffOptions = (
  preferredDate: string,
  includesDryCleaning?: boolean | null
) => {
  const days = includesDryCleaning ? STANDARD_RETURN_DAYS_DRY_CLEANING : STANDARD_RETURN_DAYS;
  return days.map((d) => addDaysToIsoDate(preferredDate, d)).filter((v): v is string => Boolean(v));
};

/**
 * Door-drop promise for every return. The window itself comes from the order
 * (see describeReturnWindow in time-windows.ts), never from a hardcoded time.
 */
export const RETURN_DISCLAIMER = "Your runner will knock, then leave your bag at your door.";
